import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';

type Variant = 'primaire' | 'secondaire' | 'danger' | 'discret';

const VARIANTS: Record<Variant, string> = {
  primaire:
    'bg-sky-500 text-slate-950 hover:bg-sky-400 focus-visible:outline-sky-300 disabled:bg-slate-600',
  secondaire:
    'bg-slate-700 text-slate-100 hover:bg-slate-600 focus-visible:outline-slate-400 disabled:bg-slate-800',
  danger:
    'bg-red-600 text-white hover:bg-red-500 focus-visible:outline-red-300 disabled:bg-slate-700',
  discret:
    'bg-transparent text-slate-300 hover:bg-slate-800 focus-visible:outline-slate-500',
};

export function Button({
  variant = 'primaire',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  return (
    <button
      {...props}
      className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-70 ${VARIANTS[variant]} ${className}`}
    />
  );
}

export function Card({
  title,
  actions,
  children,
  className = '',
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={`rounded-2xl border border-slate-700/60 bg-slate-900/60 p-5 shadow-xl shadow-slate-950/40 backdrop-blur ${className}`}
    >
      {(title || actions) && (
        <header className="mb-4 flex items-center justify-between gap-4">
          {typeof title === 'string' ? (
            <h2 className="text-lg font-semibold text-slate-100">{title}</h2>
          ) : (
            title
          )}
          {actions}
        </header>
      )}
      {children}
    </section>
  );
}

export function Field({
  label,
  hint,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: string }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-300">{label}</span>
      <input
        {...props}
        className="w-full rounded-lg border border-slate-600 bg-slate-950/70 px-3 py-2 text-slate-100 outline-none placeholder:text-slate-500 focus:border-sky-400 focus:ring-1 focus:ring-sky-400"
      />
      {hint && <span className="mt-1 block text-xs text-slate-400">{hint}</span>}
    </label>
  );
}

export function Alert({
  type = 'erreur',
  children,
}: {
  type?: 'erreur' | 'info' | 'succes';
  children: ReactNode;
}) {
  const styles = {
    erreur: 'border-red-500/50 bg-red-950/50 text-red-200',
    info: 'border-sky-500/50 bg-sky-950/40 text-sky-100',
    succes: 'border-emerald-500/50 bg-emerald-950/40 text-emerald-100',
  }[type];
  return (
    <p className={`rounded-lg border px-3 py-2 text-sm ${styles}`} role="status">
      {children}
    </p>
  );
}

export function Badge({
  children,
  tone = 'neutre',
}: {
  children: ReactNode;
  tone?: 'neutre' | 'succes' | 'alerte' | 'info';
}) {
  const styles = {
    neutre: 'bg-slate-700 text-slate-200',
    succes: 'bg-emerald-600/80 text-emerald-50',
    alerte: 'bg-red-600/80 text-red-50',
    info: 'bg-sky-600/80 text-sky-50',
  }[tone];
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${styles}`}>
      {children}
    </span>
  );
}

export function Spinner({ label = 'Chargement…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-3 text-slate-400">
      <span className="size-4 animate-spin rounded-full border-2 border-slate-500 border-t-sky-400" />
      <span className="text-sm">{label}</span>
    </div>
  );
}
